import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { useSwipeable } from "react-swipeable";
import RecipeRating from "../components/RecipeRating";
import SlidePanel from "../components/SlidePanel";
import { speakText, stopSpeaking } from '../utils/speakUtils';
import { ChevronRightIcon, ChevronLeftIcon, SpeakerWaveIcon, SpeakerXMarkIcon, MicrophoneIcon, MinusCircleIcon, XMarkIcon } from "@heroicons/react/24/solid";
import SpeakingToggleButton from '../components/cookControls/SpeakingToggleButton';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { Capacitor } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';

export default function CookModeV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSpeakingMode, setIsSpeakingMode] = useState(false);
  const [unrecognized, setUnrecognized] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  //const wakeLockRef = useRef(null);
  const [rating, setRating] = useState(recipe?.rating || null);

  const [searchParams] = useSearchParams();
  const servings = searchParams.get("serve");
  console.log("serving:", servings);


  // Control loop ref
  const listeningRef = useRef(false);
  // Activation phrase
  const activationTimeoutRef = useRef(null);

  // Load recipe
  /*
  useEffect(() => {
    apiClient.get(`/recipes/recipe/${id}`)
      .then(res => {setRecipe(res.data);})
      .catch(err => console.error("Failed to load recipe:", err));
  }, [id]); */

  useEffect(() => {
    const loadRecipe = async () => {
      try {
        if (servings) {
          const res = await apiClient.post("/recipes/scale", { recipeId: id, servings });
          setRecipe(res.data.recipe);
        } else {
          const res = await apiClient.get(`/recipes/recipe/${id}`);
          setRecipe(res.data);
        }
      } catch (err){
        console.log("Failed to load recipe");
        return("Something went wrong :/");
      }
    };
    loadRecipe();
  }, [id, servings]);


  // VOICE CONTROLS
  // Start/stop native listening based on toggle
  useEffect(() => {
    if (!recipe) return;
    listeningRef.current = isListening;
    if (isListening) {
      startNativeListening();
    } else {
      stopListening();
    }
    return () => stopListening();
  }, [recipe, isListening]);

  // Constant native listening loop
  const startNativeListening = async () => {
    try {
      const perm = await SpeechRecognition.checkPermissions();
      if (perm.speechRecognition !== 'granted') {
        const req = await SpeechRecognition.requestPermissions();
        if (req.speechRecognition !== 'granted') return;
      }
      // remove listeners
      await SpeechRecognition.removeAllListeners();
      // loop
      const listenLoop = async () => {
        if (!listeningRef.current) return;
        let result;
        try {
          result = await SpeechRecognition.start({
            language: 'en-US',
            maxResults: 1,
            partialResults: false,
            popup: false,
          });
        } catch (e) {
          console.warn('Recognition error:', e);
          await new Promise(r => setTimeout(r, 500));
        }
        if (result?.matches?.length) {
          handleTranscript(result.matches[0].trim().toLowerCase());
        }
        // loop again
        listenLoop();
      };
      listenLoop();
    } catch (e) {
      console.warn('startNativeListening error:', e);
    }
  };

  // Stop native listening
  const stopListening = () => {
    listeningRef.current = false;
    clearTimeout(activationTimeoutRef.current);
    SpeechRecognition.stop().catch(() => {});
    SpeechRecognition.removeAllListeners().catch(() => {});
  };

// Handle commands with activation phrase
const handleTranscript = txt => {
  const normalized = txt.trim().toLowerCase();
  const match = normalized.match(/^ok\s+chef\s+(next|back|repeat|stop reading|start reading)$/);
  if (match) {
    const command = match[1];
    switch (command) {
      case 'next':
        setCurrentStep(s => Math.min(s + 1, recipe.instructions.length - 1));
        break;
      case 'go back':
        setCurrentStep(s => Math.max(s - 1, 0));
        break;
      case 'repeat':
        speakText(recipe.instructions[currentStep]);
        break;
      case 'stop reading':
        stopSpeaking();
        setIsSpeakingMode(false);
        break;
      case 'start reading':
        speakText(recipe.instructions[currentStep]);
        setIsSpeakingMode(true);
        break;
      default:
        break;
    }
  } else {
    setUnrecognized(true);
    setTimeout(() => setUnrecognized(false), 1500);
  }
};

  // READING CONTROLS
  // Speaking toggle immediate UI
  const toggleSpeakingMode = useCallback(() => {
    if (!recipe) return;
    if (isSpeakingMode) {
      stopSpeaking();
      setIsSpeakingMode(false);
    } else {
      setIsSpeakingMode(true);
      speakText(recipe.instructions[currentStep]);
    }
  }, [recipe, currentStep, isSpeakingMode]);

  // Auto-read on step change
  useEffect(() => {
    if (isSpeakingMode && recipe) speakText(recipe.instructions[currentStep]);
  }, [currentStep, isSpeakingMode, recipe]);

  // Swipe handlers
  const handlers = useSwipeable({
    onSwipedLeft: () => recipe && currentStep < recipe.instructions.length - 1 && setCurrentStep(s => s + 1),
    onSwipedRight: () => recipe && currentStep > 0 && setCurrentStep(s => s - 1),
    preventDefaultTouchmoveEvent: true,
    trackTouch: true,
  });

  useEffect(() => {
    const activateWakeLock = async () => {
      try {
        const supported = await KeepAwake.isSupported();
        if (supported.isSupported) {
          await KeepAwake.keepAwake();
          console.log("Wake lock enabled");
        } else {
          console.warn("KeepAwake not supported on this platform");
        }
      } catch (err) {
        console.error("Failed to enable KeepAwake:", err);
      }
    };
  
    activateWakeLock();
  
    return () => {
      KeepAwake.allowSleep().catch(() => {});
      console.log("Wake lock released");
    };
  }, []);


  if (!recipe) {
    return <div className="flex items-center justify-center h-screen text-gray-600">Loading recipe...</div>;
  }

  const isNative = ['ios', 'android'].includes(Capacitor.getPlatform());
  const totalSteps = recipe.instructions.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {unrecognized && (
        <div className="fixed top-4 right-4 bg-red-100 text-red-800 px-4 py-2 rounded shadow-lg z-50 text-sm">
          🤔 Sorry, I didn’t catch that.
        </div>
      )}

      {/* Control Panel */}
      <div className="flex items-center justify-end p-4 bg-white shadow">
      {/*isNative && (
          <button
            onClick={() => setIsListening(l => !l)}
            className={`flex items-center mr-3 px-3 py-1 rounded-full text-sm font-medium ${isListening ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            <MicrophoneIcon className="w-6 h-6 mr-2" />
            {isListening ? 'Voice On' : 'Voice Off'}
          </button>
      )*/}
        <SpeakingToggleButton
          isSpeakingMode={isSpeakingMode}
          toggleSpeakingMode={toggleSpeakingMode}
          disabled={isBusy}
        />
        <button
          onClick={() => navigate(-1)}
          className="flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700"
        >
           <XMarkIcon className="w-6 h-6" /> Exit
        </button>
      </div>

      {/* Title & Stepper */}
      <div className="w-full bg-white shadow py-4 px-6">
        <h1 className="text-3xl font-bold text-gray-800 text-center truncate">{recipe.name}</h1>
        <div className="mt-2 flex items-center justify-center space-x-2">
          <p className="text-gray-600">Step {currentStep + 1} of {totalSteps}</p>
          <div className="relative w-48 h-2 bg-gray-200 rounded-full">
            <div className="absolute h-2 bg-green-500 rounded-full" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="flex flex-col md:flex-row flex-1 p-4 space-y-4 md:space-y-0 md:space-x-6">
        <div {...handlers} className="w-full md:w-2/3 bg-gradient-to-b from-blue-50 to-indigo-100 rounded-lg shadow p-6">
          <p className="text-lg text-gray-800 leading-relaxed mb-6">{recipe.instructions[currentStep]}</p>
          <div className="flex justify-between items-center">
            <button
              onClick={() => setCurrentStep(s => Math.max(s - 1, 0))}
              disabled={currentStep === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
            >
              <ChevronLeftIcon className="w-6 h-6" />
              <span>Back</span>
            </button>
            <p className="text-xs text-gray-400 italic">Swipe left/right</p>
            <button
              onClick={() => setCurrentStep(s => Math.min(s + 1, totalSteps - 1))}
              disabled={currentStep === totalSteps - 1}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50"
            >
              <span>Next</span>
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Rating */}
        {currentStep === totalSteps - 1 && (
          <div className="p-4">
            <div className="w-full bg-amber-100 border-l-4 border-amber-400 p-4 rounded-lg shadow">
              <p className="font-semibold text-gray-800">Enjoyed this recipe?</p>
              <p className="text-sm text-gray-700 mb-3">Rate it so we can keep personalising!</p>
              <RecipeRating
                recipeId={recipe._id}
                initialRating={rating}
                onRated={setRating}
              />
            </div>
          </div>
        )}

        {/* Ingredients */}
        <div className="w-full md:w-1/3 bg-white rounded-lg shadow p-6 overflow-y-auto max-h-[60vh]">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Ingredients</h2>
          <ul className="space-y-2 text-gray-700 text-sm">
            {recipe.ingredients.map((item, idx) => (
              item.isHeading ? (
                <li key={idx} className="font-medium text-gray-800 border-b border-gray-200 pb-1">{item.text}</li>
              ) : (
                <li key={idx} className="flex justify-between">
                  <span>{item.text}</span>
                  <span className="font-semibold">{item.quantity}</span>
                </li>
              )
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
